import * as analytics from 'lib/analytics'
import createActionConstants from 'lib/create-action-constants'

export const constants = createActionConstants('report', [
  'SET_PUBLISHED_RANGE',
  'LOAD_REPORT',

  'FETCH_PAPER',
  'FETCH_PAPER_SUCCESS',
  'FETCH_PAPER_FAILURE',

  'FETCH_CITING_COUNTS',
  'FETCH_CITING_COUNTS_SUCCESS',
  'FETCH_CITING_COUNTS_FAILURE',

  'FETCH_REPORT',
  'FETCH_REPORT_SUCCESS',
  'FETCH_REPORT_FAILURE',
  'FETCH_CITATIONS_PROGRESS',
  'FETCH_CITATIONS_SUCCESS',
  'FETCH_REFERENCES_SUCCESS',

  'FLUSH_REPORT',

  'FETCH_CITATIONS_BY_ISSN',

  'UPDATE_FLAG',

  'FLAG_CITATION',
  'FLAG_CITATION_SUCCESS',
  'FLAG_CITATION_FAILURE',

  'TOGGLE_FILTER',
  'RESET_FILTERS',

  'CLAIM_PAPER',
  'CLAIM_PAPER_SUCCESS',
  'CLAIM_PAPER_FAILURE',
  'FLUSH_CLAIM_PAPER_TRACKER',

  'SET_TEXT_MATCH',

  'HANDLE_QUERY_CHANGE',

  'EXPORT_REPORT',
  'EXPORT_REPORT_SUCCESS',
  'EXPORT_REPORT_FAILURE',

  'FETCH_MORE_LIKE_THIS',
  'FETCH_MORE_LIKE_THIS_SUCCESS',
  'FETCH_MORE_LIKE_THIS_FAILURE',

  'SET_SORT',
  'SET_SORT_VALUE'
])

export const setPublishedRange = value => ({
  type: constants.SET_PUBLISHED_RANGE,
  meta: {
    debounce: {
      time: 300
    },
    queryUpdate: {
      update: {
        page: 'report.filters.currentPage',
        publishedRangeMin: 'report.filters.publishedRange.min',
        publishedRangeMax: 'report.filters.publishedRange.max'
      }
    }
  },
  payload: value
})

/**
* Loads a report from a report object, can loaded
* partial reports.
* @param {object} report - report
* @param {object} report.paper - main paper
* @param {object} report.tally - tally
* @param {array}  report.citations - citations
* @param {object} report.papers - extra papers for use as citation sources
*/
export const loadReport = report => {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('loading report', report)
  }

  return {
    type: constants.LOAD_REPORT,
    payload: report
  }
}

/**
 * Fetches a paper and its tally from the API
 * and then loads them.
 * @param {string} doi - doi to load
 */
export const fetchPaper = doi => ({
  type: constants.FETCH_PAPER,
  payload: doi
})

/**
 * Fetches a citing counts (for filters when we don't
 * have a claim on the data) from the API
 * @param {string} doi - doi to load
 */
export const fetchCitingCounts = doi => ({
  type: constants.FETCH_CITING_COUNTS,
  payload: doi
})

/**
 * Fetches whole report, only loading
 * paper/tally data if we don't already have it.
 * @param {string} doi - source doi to load
 */
export const fetchReport = doi => ({
  type: constants.FETCH_REPORT,
  payload: doi
})

export const flushReport = () => ({
  type: constants.FLUSH_REPORT
})

/**
 * Set cite sort
 */
export const setSort = sort => ({
  type: constants.SET_SORT,
  payload: sort
})

/**
 * Toggles filter
 * @param {string} key - filter to toggle
 * @param {boolean} [val] - value to set to if you don't want to toggle
 */
export const toggleFilter = (key, val, opts = {}) => {
  analytics.event({
    category: 'Filter Toggle',
    action: key
  })

  return {
    type: constants.TOGGLE_FILTER,
    meta: {
      queryUpdate: {
        update: {
          page: 'report.filters.currentPage',
          [key]: `report.filters.${key}`
        },
        prune: opts.pruneUrl
      }
    },
    payload: {
      key,
      val
    }
  }
}

/**
 * Reset filters
 * */
export const resetFilters = () => ({
  type: constants.RESET_FILTERS
})

/**
 * Updates flag
 * @param {object} flag - flag properties to update
 */
export const updateFlag = flag => ({
  type: constants.UPDATE_FLAG,
  payload: flag
})

/**
 * Flags citation on API server
 * @param {object} user - user posting the flag
 * @param {object} flag - form data describing the flag
 */
export const flagCitation = (user, flag) => ({
  type: constants.FLAG_CITATION,
  payload: {
    user, flag
  }
})

export const claimPaper = (user, paper) => ({
  type: constants.CLAIM_PAPER,
  payload: {
    user, paper
  }
})

export const flushClaimPaperTracker = () => ({
  type: constants.FLUSH_CLAIM_PAPER_TRACKER
})

export const setTextMatch = (text, debounce = false) => {
  const debouncer = debounce ? { debounce: { time: 300 } } : {}

  return {
    type: constants.SET_TEXT_MATCH,
    meta: {
      ...debouncer,
      queryUpdate: {
        update: {
          page: 'report.filters.currentPage',
          textMatch: 'report.filters.textMatch'
        }
      }
    },
    payload: text
  }
}

export const handleQueryChange = query => ({
  type: constants.HANDLE_QUERY_CHANGE,
  payload: query
})

export const exportReport = (doi, opts) => {
  analytics.event({
    category: 'Report',
    action: 'export-report'
  })

  return {
    type: constants.EXPORT_REPORT,
    payload: {
      doi, opts
    }
  }
}

export const fetchMoreLikeThis = like => {
  return {
    type: constants.FETCH_MORE_LIKE_THIS,
    payload: like
  }
}
